import { Input } from "@fluentui/react-components"
import type { ProblemDetails } from "@rema/shared"
import type { Domain } from "@rema/shared/schemas/domains"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { ApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { domainsI18n as i18n } from "./i18n"

// Transferencia de maestro (F4.6): el nuevo maestro debe pertenecer al dominio; el
// servidor valida la pertenencia y devuelve el error por campo si no aplica.
interface TransferFormValues {
  userId: string
}

export interface TransferMasterDrawerProps {
  open: boolean
  domain: Domain | null
  onClose: () => void
  onTransfer: (id: string, userId: string) => Promise<unknown>
}

export const TransferMasterDrawer = ({
  open,
  domain,
  onClose,
  onTransfer,
}: TransferMasterDrawerProps) => {
  const { control, handleSubmit, reset, setError, formState } = useForm<TransferFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { userId: "" },
  })

  useEffect(() => {
    if (open) reset({ userId: "" })
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!domain) return
    try {
      await onTransfer(domain.id, values.userId.trim())
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        // El backend marca el campo "user_id"; se mapea al input del formulario.
        const mapped = applyFieldErrors(remapUserIdField(error.problem), setError)
        if (!mapped) setError("root", { message: problemMessage(error.problem) })
      } else {
        throw error
      }
    }
  })

  return (
    <AppDrawerForm
      open={open}
      size="small"
      title={i18n.transfer.title}
      submitLabel={i18n.transfer.submit}
      submitting={formState.isSubmitting}
      error={formState.errors.root?.message ?? null}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <FormField
        control={control}
        name="userId"
        label={i18n.transfer.userIdLabel}
        required
        hint={i18n.transfer.userIdHint}
        rules={{ required: i18n.transfer.userIdRequired }}
      >
        {(field, invalid) => <Input {...field} placeholder="usr_…" aria-invalid={invalid} />}
      </FormField>
    </AppDrawerForm>
  )
}

// El contrato usa "user_id"; react-hook-form usa "userId".
function remapUserIdField(problem: ProblemDetails): ProblemDetails {
  if (!problem.errors) return problem
  return {
    ...problem,
    errors: problem.errors.map((fieldError) =>
      fieldError.field === "user_id" ? { ...fieldError, field: "userId" } : fieldError,
    ),
  }
}
